<script module lang="ts">
  import { marked, type Tokens } from 'marked';
  import { sanitizeUrl } from '../sanitizeUrl';

  // Configuración global de marked (una sola vez por módulo).
  // El renderer de links sanea el href: marked autolinkea URLs pegadas a
  // residuos de texto (`...html)//n`) y sin esto el href queda contaminado.
  marked.use({
    breaks: true,
    gfm: true,
    renderer: {
      link(this: { parser?: { parseInline: (tokens: Tokens.Generic[]) => string } }, token: Tokens.Link): string {
        const href = sanitizeUrl(token.href);
        const text = this.parser?.parseInline(token.tokens) ?? token.text ?? '';
        if (!href) return text;
        const title = token.title ? ` title="${token.title.replace(/"/g, '&quot;')}"` : '';
        return `<a href="${href}"${title}>${text}</a>`;
      },
    },
  });
</script>

<script lang="ts">
  import { tick } from 'svelte';

  export type DocumentHeading = { id: string; text: string; level: number };

  let {
    content = '',
    palette,
    color = undefined,
    variant = 'default',
    onHeadings = undefined,
  } = $props<{
    content?: string;
    palette: Record<string, string>;
    color?: string;
    variant?: 'default' | 'document';
    /** Notifica los encabezados del documento (con id de ancla) al lector. */
    onHeadings?: (headings: DocumentHeading[]) => void;
  }>();

  // Parse markdown to HTML
  let html = $derived(
    marked.parse(content || '', variant === 'document' ? { breaks: false } : undefined)
  );
  let fg = $derived(color || palette.textPrimary);
  let markdownElement: HTMLDivElement | null = null;

  function slugify(text: string): string {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80);
  }

  /** Asigna ids estables a los encabezados y publica el índice del documento. */
  function configureDocumentHeadings(): void {
    if (!markdownElement || variant !== 'document') return;

    const usados = new Set<string>();
    const headings: DocumentHeading[] = [];

    markdownElement.querySelectorAll<HTMLElement>('h2, h3, h4').forEach((el) => {
      const text = (el.textContent ?? '').trim();
      const base = slugify(text) || 'seccion';
      let id = base;
      let sufijo = 2;
      while (usados.has(id)) {
        id = `${base}-${sufijo}`;
        sufijo += 1;
      }
      usados.add(id);
      el.id = id;
      headings.push({ id, text, level: Number(el.tagName.slice(1)) });
    });

    onHeadings?.(headings);
  }

  function configureRenderedLinks() {
    markdownElement?.querySelectorAll('a[href]').forEach((link) => {
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
    });
    configureDocumentHeadings();
  }

  $effect(() => {
    html;
    void tick().then(configureRenderedLinks);
  });
</script>

<div
  bind:this={markdownElement}
  class="markdown-content"
  class:document={variant === 'document'}
  style={`color: ${fg}; --md-border: color-mix(in srgb, ${fg} 20%, transparent); --md-bg: color-mix(in srgb, ${fg} 5%, transparent); --md-muted: color-mix(in srgb, ${fg} 70%, transparent); --md-paper: ${palette.bgChatInput || palette.bgCard || 'transparent'};`}
>
  {@html html}
</div>

<style>
  .markdown-content {
    line-height: 1.6;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .markdown-content :global(*) {
    max-width: 100%;
  }

  .markdown-content.document {
    max-width: 74ch;
    margin: 0 auto;
    padding: clamp(22px, 3.4vw, 52px) clamp(18px, 4vw, 58px);
    border: 1px solid var(--md-border);
    border-radius: 10px;
    background: color-mix(in srgb, var(--md-paper) 96%, currentColor 4%);
    box-shadow: 0 14px 34px rgba(3, 12, 32, 0.09);
    font-family: Georgia, 'Times New Roman', serif;
    font-size: 16px;
    line-height: 1.75;
    hyphens: auto;
  }

  .markdown-content.document :global(p) {
    margin: 0 0 1em;
    text-align: justify;
    text-indent: 1.5em;
  }

  .markdown-content.document :global(h1),
  .markdown-content.document :global(h2),
  .markdown-content.document :global(h3),
  .markdown-content.document :global(h4) {
    font-family: inherit;
    line-height: 1.3;
    text-align: left;
  }

  /* Navegación por anclas del lector de biblioteca */
  .markdown-content.document :global(h2),
  .markdown-content.document :global(h3),
  .markdown-content.document :global(h4) {
    scroll-margin-top: 88px;
    text-wrap: balance;
  }

  /* El primer párrafo de una sección no lleva sangría francesa */
  .markdown-content.document :global(h2 + p),
  .markdown-content.document :global(h3 + p),
  .markdown-content.document :global(h4 + p),
  .markdown-content.document :global(p:first-child) {
    text-indent: 0;
  }

  .markdown-content.document :global(h4) {
    margin-top: 1.5em;
    color: var(--biblioteca-accent, currentColor);
    font-size: 1.04em;
    font-weight: 700;
    letter-spacing: 0.01em;
  }

  .markdown-content.document :global(blockquote) {
    border-left: 3px solid var(--biblioteca-accent, var(--md-border));
    background: var(--md-bg);
    border-radius: 0 6px 6px 0;
    padding: 0.45em 0.9em;
  }
  
  /* Headings */
  .markdown-content :global(h1) {
    font-size: 1.5em;
    font-weight: 700;
    margin: 0.8em 0 0.4em 0;
    border-bottom: 2px solid var(--md-border);
    padding-bottom: 0.3em;
  }
  
  .markdown-content :global(h2) {
    font-size: 1.3em;
    font-weight: 700;
    margin: 0.7em 0 0.4em 0;
  }
  
  .markdown-content :global(h3) {
    font-size: 1.15em;
    font-weight: 600;
    margin: 0.6em 0 0.3em 0;
  }
  
  .markdown-content :global(h4) {
    font-size: 1.05em;
    font-weight: 600;
    margin: 0.5em 0 0.3em 0;
  }
  
  /* Paragraphs */
  .markdown-content :global(p) {
    margin: 0.5em 0;
  }
  
  /* Lists */
  .markdown-content :global(ul),
  .markdown-content :global(ol) {
    margin: 0.5em 0;
    padding-left: 1.8em;
  }
  
  .markdown-content :global(li) {
    margin: 0.3em 0;
  }
  
  .markdown-content :global(ul) {
    list-style-type: disc;
  }
  
  .markdown-content :global(ul ul) {
    list-style-type: circle;
  }
  
  .markdown-content :global(ul ul ul) {
    list-style-type: square;
  }
  
  /* Bold and italic */
  .markdown-content :global(strong) {
    font-weight: 700;
  }
  
  .markdown-content :global(em) {
    font-style: italic;
  }
  
  /* Code */
  .markdown-content :global(code) {
    background-color: var(--md-bg);
    padding: 0.15em 0.4em;
    border-radius: 3px;
    font-family: 'Courier New', monospace;
    font-size: 0.9em;
    white-space: break-spaces;
    overflow-wrap: anywhere;
    word-break: break-word;
  }
  
  .markdown-content :global(pre) {
    background-color: var(--md-bg);
    padding: 0.8em;
    border-radius: 4px;
    overflow-x: auto;
    margin: 0.5em 0;
    width: 100%;
    max-width: 100%;
    overscroll-behavior-x: contain;
  }
  
  .markdown-content :global(pre code) {
    background-color: transparent;
    padding: 0;
    white-space: pre;
    overflow-wrap: normal;
    word-break: normal;
  }
  
  /* Blockquotes */
  .markdown-content :global(blockquote) {
    border-left: 3px solid var(--md-border);
    padding-left: 1em;
    margin: 0.5em 0;
    color: var(--md-muted);
  }
  
  /* Horizontal rules */
  .markdown-content :global(hr) {
    border: none;
    border-top: 2px solid var(--md-border);
    margin: 1em 0;
  }
  
  /* Links */
  .markdown-content :global(a) {
    color: #0066cc;
    text-decoration: none;
    overflow-wrap: anywhere;
    word-break: break-word;
  }
  
  .markdown-content :global(a:hover) {
    text-decoration: underline;
  }
  
  /* Tables */
  .markdown-content :global(table) {
    border-collapse: collapse;
    width: 100%;
    max-width: 100%;
    display: block;
    overflow-x: auto;
    overscroll-behavior-x: contain;
    -webkit-overflow-scrolling: touch;
    margin: 0.5em 0;
  }
  
  .markdown-content :global(th),
  .markdown-content :global(td) {
    border: 1px solid var(--md-border);
    padding: 0.4em 0.8em;
    text-align: left;
  }
  
  .markdown-content :global(th) {
    background-color: var(--md-bg);
    font-weight: 600;
  }

  .markdown-content :global(img),
  .markdown-content :global(video),
  .markdown-content :global(svg) {
    display: block;
    max-width: 100%;
    height: auto;
  }

  @media (max-width: 768px) {
    .markdown-content {
      line-height: 1.52;
    }

    .markdown-content :global(h1) { font-size: 1.3em; }
    .markdown-content :global(h2) { font-size: 1.18em; }
    .markdown-content :global(h3) { font-size: 1.08em; }

    .markdown-content :global(ul),
    .markdown-content :global(ol) {
      padding-left: 1.35em;
    }

    .markdown-content :global(blockquote) {
      padding-left: 0.7em;
      margin-left: 0;
      margin-right: 0;
    }

    .markdown-content :global(th),
    .markdown-content :global(td) {
      padding: 0.35em 0.55em;
      min-width: 110px;
    }

    .markdown-content.document {
      padding: 20px 16px;
      font-size: 15px;
      line-height: 1.68;
    }

    /* En pantallas angostas el justificado genera ríos de espacio */
    .markdown-content.document :global(p) {
      text-align: left;
    }
  }
</style>
